// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Card, Typography } from '@altermaind/web/ui';
import { SwitchDarkOrLightMode } from '@/_shared/components/SwitchDarkOrLightMode';
import { Tooltip } from '@/_shared/components/Tooltip/Tooltip';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard, WizardNavigateCallback } from '@/_shared/hooks/useWizard';
import { getAspectColorsCall, putAspectSaveCall, useAspect } from '@/aspect';
import { WIZARD_STEPS } from '@/configuration/constants/wizard';

export const SetColor = () => {
  const t = useTranslations('configuration.hr.setColor');
  const sharedT = useTranslations();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    setCustomOnNext,
    setCustomOnSkip,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();

  const { dark, setDark, gapXS, gapXXL, colorSuggested, onSelectedNewColor } = useAspect();

  const {
    watch,
    formState: { isValid },
    handleSubmit,
  } = useForm();

  const fieldValues = watch();

  const {
    execute: executeGetAspectColors,
    response: responseGetAspectColors,
    success: successGetAspectColors,
  } = getAspectColorsCall.hook();

  const { execute: executePutAspectSave, loading: loadingPutAspectSave } = putAspectSaveCall.hook();

  const someInputFilled = useMemo(() => {
    return Object.keys(fieldValues).some((key: string) => !!fieldValues[key]);
  }, [fieldValues]);

  const onSubmit = useCallback(
    (navigate?: WizardNavigateCallback) => {
      if (colorSuggested && responseGetAspectColors) {
        executePutAspectSave({
          colorId: colorSuggested,
          successSaga: () => {
            navigate?.();
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePutAspectSave, sharedT, colorSuggested, responseGetAspectColors],
  );

  const onSkip: NonNullable<Parameters<typeof setCustomOnSkip>[0]> = useCallback(navigate => {
    navigate?.(WIZARD_STEPS.SET_COMPANY_DATA);
  }, []);

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
    setCustomOnSkip(onSkip);

    if (!successGetAspectColors) {
      executeGetAspectColors({
        successSaga: ({ response }) => {
          if (colorSuggested) {
            onSelectedNewColor(colorSuggested);
          } else {
            onSelectedNewColor(response.colors[0].id);
          }
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    }

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    executeGetAspectColors,
    handleSubmit,
    onSkip,
    onSubmit,
    resetWizardNavigationCallbacks,
    setCustomOnNext,
    setCustomOnSkip,
    successGetAspectColors,
    onSelectedNewColor,
  ]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: !colorSuggested || loadingPutAspectSave,
      isLoadingNextButton: loadingPutAspectSave,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [
    colorSuggested,
    isValid,
    loadingPutAspectSave,
    resetWizardNavigationButtonStatus,
    setNavigationButtonStatus,
    someInputFilled,
  ]);

  return (
    <Box mb='s'>
      <Box
        display='flex'
        flex={10}
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        style={{ gap: gapXXL }}
      >
        <Box
          width='100%'
          display='flex'
          flexDirection='column'
          justifyContent='left'
          alignItems='left'
          textAlign='left'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 6 },
            defaultValue: 10,
          })}
        >
          <Box mb='m'>
            <Typography
              variant='h1'
              color='primary/text'
              fontSize={genericResponsiveValue({
                breakPoints: { desktop: 50 as never },
                defaultValue: 'xxxl',
              })}
              lineHeight={genericResponsiveValue({
                breakPoints: { desktop: '60px' as never },
                defaultValue: 'xxxl',
              })}
              fontWeight='bold'
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'm' },
                defaultValue: 'xs',
              })}
            >
              {t('title')}
            </Typography>
            <Typography variant='h4' fontWeight={200} mt='s' color='primary/text'>
              {t('subtitle')}
            </Typography>
          </Box>
          <Box
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            display='flex'
            style={{ gap: gapXS }}
          >
            <Box
              display={genericResponsiveValue({
                breakPoints: { desktop: 'grid' },
                defaultValue: 'flex',
              })}
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              justifyContent='space-between'
              style={{ gap: gapXS }}
              gridTemplateColumns={`repeat(${genericResponsiveValue({
                breakPoints: { desktop: 2 },
                defaultValue: 1,
              })}, 1fr)`}
              width='100%'
            >
              <Box width='100%' gridColumn='1/span 2' flex={1}>
                <Typography variant='h4' mb='s'>
                  {t('titleColor')}
                </Typography>
                <Box
                  mb='m'
                  display='flex'
                  flexDirection='row'
                  flexWrap={genericResponsiveValue({
                    breakPoints: { desktop: 'no-wrap' },
                    defaultValue: 'wrap',
                  })}
                >
                  {responseGetAspectColors &&
                    responseGetAspectColors.colors.map(color => (
                      <Box
                        mr='m'
                        borderRadius='xl'
                        p='xxxs'
                        border={
                          colorSuggested === color.id ? '1px solid white' : '1px solid transparent'
                        }
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        key={color.id}
                        style={{
                          cursor: 'pointer',
                          transition: 'border 0.2s',
                        }}
                        mb={genericResponsiveValue({
                          breakPoints: { desktop: 'none' },
                          defaultValue: 'xs',
                        })}
                        onClick={() => onSelectedNewColor(color.id)}
                      >
                        <Box
                          borderRadius='xl'
                          width='m'
                          height='m'
                          style={{
                            backgroundColor: color.dark,
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          display='flex'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 4 },
            defaultValue: 10,
          })}
          style={{ gap: gapXS }}
          flexDirection='row'
          flexWrap='wrap'
          width='100%'
        >
          <Box width='100%'>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='space-between'
              mb='xs'
            >
              <Box
                textAlign='center'
                mb='xs'
                alignItems='center'
                display='flex'
                justifyContent='center'
              >
                <Typography variant='p2/light'>{t('previewLabel')}</Typography>
                <Tooltip
                  iconProps={{ size: 'xs', fill: 'grey/light' }}
                  containerProps={{ ml: 'xxs' }}
                >
                  {t('previewLabelTooltip')}
                </Tooltip>
              </Box>
              <Card>
                {dark ? (
                  <Box
                    display='flex'
                    borderRadius='s'
                    backgroundColor={
                      responseGetAspectColors?.colors.find(color => color.id === colorSuggested)
                        ?.dark as never
                    }
                  >
                    <Asset
                      width={genericResponsiveValue({
                        breakPoints: { desktop: '500px', tablet: '500px' },
                        defaultValue: '100%',
                      })}
                      domain='images'
                      name='configExampleDark'
                    />
                  </Box>
                ) : (
                  <Box
                    display='flex'
                    borderRadius='s'
                    backgroundColor={
                      responseGetAspectColors?.colors.find(color => color.id === colorSuggested)
                        ?.light as never
                    }
                  >
                    <Asset
                      width={genericResponsiveValue({
                        breakPoints: { desktop: '500px', tablet: '500px' },
                        defaultValue: '100%',
                      })}
                      domain='images'
                      name='configExampleLight'
                    />
                  </Box>
                )}
              </Card>
              <Box display='flex' justifyContent='center' mt='s'>
                <SwitchDarkOrLightMode
                  isDarkMode={dark}
                  onClick={() => setDark(!dark)}
                  name='darkMode'
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
